<template>
  <div>
    <div v-if="adsMetaData !== null && adCopy" class="container main">
      <!-- Ad details -->
      <div class="box">
        <div class="field">
          <label class="label">Ad type</label>
          <div class="control">
            <div class="select">
              <select v-model="adCopy.adTypeId">
                <option
                  v-for="adType in adsMetaData.types"
                  :key="adType.id"
                  v-bind:value="adType.id"
                  >{{ adType.name }}</option
                >
              </select>
            </div>
          </div>
        </div>

        <div class="field">
          <label class="label">Customer</label>
          <div class="control">
            <div class="select">
              <select v-model="adCopy.customerId">
                <option
                  v-for="customer in adsMetaData.customers"
                  :key="customer.id"
                  v-bind:value="customer.id"
                  >{{ customer.name }}</option
                >
              </select>
            </div>
          </div>
        </div>

        <div class="field">
          <label class="label">Target URL</label>
          <div class="control">
            <input class="input" type="text" v-model="adCopy.targetURL" :disabled="isSaving" />
          </div>
          <p class="help is-danger" v-if="!isTargetURLValid">Target URL is required</p>
        </div>

        <div class="field">
          <label class="label">Title</label>
          <div class="control">
            <input class="input" type="text" v-model="adCopy.title" :disabled="isSaving" />
          </div>
          <p class="help is-danger" v-if="!isTitleValid">Title is required</p>
        </div>

        <div class="field" v-if="adTextAvailable">
          <label class="label">Text</label>
          <div class="control">
            <textarea
              class="textarea"
              placeholder="Empty"
              v-model="adCopy.text"
              :disabled="isSaving"
            />
          </div>
        </div>

        <!-- Languages -->
        <div class="field">
          <label class="label">Language filter</label>
          <div class="control">
            <div class="select">
              <select v-model="adCopy.languageFilterEnabled">
                <option v-bind:value="false">Disabled (ad visible for all languages)</option>
                <option v-bind:value="true">Enabled</option>
              </select>
            </div>
          </div>
        </div>

        <div v-if="adCopy.languageFilterEnabled && adsMetaData">
          <div class="field" v-for="languageCode in adsMetaData.languageCodes" :key="languageCode">
            <div class="control">
              <label class="checkbox">
                <input
                  type="checkbox"
                  :value="languageCode"
                  v-model="adCopy.languageCodes"
                  :disabled="isSaving"
                />
                {{ languageCode }}
              </label>
            </div>
          </div>

          <div class="field has-addons">
            <div class="control">
              <input
                class="input"
                type="text"
                placeholder="Language code"
                v-model="newLanguageCode"
                :disabled="isSaving || isAddingLanguageCode"
              />
            </div>

            <div class="control">
              <a
                class="button is-primary"
                :disabled="!isNewLanguageCodeValid || isSaving || isAddingLanguageCode"
                :class="{ 'is-loading': isAddingLanguageCode }"
                @click="addLanguageCode"
              >
                Add
              </a>
            </div>

            <div class="notification is-danger" v-if="addLanguageCodeError">
              {{ addLanguageCodeError }}
            </div>
          </div>
        </div>

        <div class="button-row">
          <!-- Save button -->
          <a
            class="button is-primary"
            @click="saveAd"
            :disabled="hasValidationErrors || !isDirty || isSaving"
            :class="{ 'is-loading': isSaving }"
          >
            Save
          </a>

          <!-- Save error -->
          <div class="notification is-danger" v-if="saveError">
            {{ saveError }}
          </div>
        </div>
      </div>

      <!-- Image -->
      <div class="box">
        <label class="label">Image</label>

        <div class="image-container">
          <div class="image-scroll-area" v-if="!imageLoadFailed">
            <img
              class="adImage"
              :src="adImageURL"
              @load="onImageLoaded"
              @error="onImageLoadError"
            />
          </div>

          <p class="image-missing" v-if="imageLoadFailed">[No image]</p>

          <div class="overlay" v-if="!imageIsLoaded && !imageLoadFailed">
            <Spinner />
          </div>
        </div>

        <div class="file">
          <label class="file-label">
            <input
              class="file-input"
              type="file"
              name="resume"
              @change="uploadImage($event.target.files)"
              :disabled="isSaving || isUploadingImage"
            />
            <span class="file-cta">
              <span class="file-icon">
                <i class="icon-upload" aria-hidden="true"></i>
              </span>
              <span class="file-label">
                Upload image…
              </span>
              <Spinner v-if="isUploadingImage" width="16px" height="16px" />
            </span>
          </label>
        </div>

        <!-- Image upload error -->
        <div class="notification is-danger" v-if="imageUploadError">
          {{ imageUploadError }}
        </div>
      </div>

      <!-- Scheduling -->
      <div class="box" v-if="adsMetaData !== null && adCopy">
        <label class="label">Scheduling</label>

        <Spinner v-if="isLoadingPeriods" />

        <div class="notification is-danger" v-if="periodsLoadError">
          {{ periodsLoadError }}
        </div>

        <div class="table-container" v-if="sortedPeriods">
          <table class="table periods-table">
            <tr>
              <th></th>
              <th>From</th>
              <th>To</th>
              <th></th>
            </tr>

            <tr v-for="period in sortedPeriods" :key="period.id">
              <td>
                <span
                  v-if="periodStatusText(period)"
                  class="tag"
                  :class="periodStatusTagClass(period)"
                  >{{ periodStatusText(period) }}</span
                >
              </td>
              <td>{{ period.startTimestamp }}</td>
              <td>{{ period.endTimestamp }}</td>
              <td>
                <a @click="editPeriod(period)">
                  <span>
                    <i class="icon-edit-2"></i>
                  </span>
                </a>

                <a @click="deletePeriod(period)" class="ml-4">
                  <span class="has-text-danger">
                    <i class="icon-trash-2"></i>
                  </span>
                </a>
              </td>
            </tr>
          </table>
        </div>

        <a class="button is-primary" @click="addPeriod" :disabled="isSaving">
          Add period
        </a>
      </div>
    </div>

    <AdEditPeriodModal
      v-if="adPeriodToEdit"
      :adsStorePath="adsStorePath"
      :adPeriod="adPeriodToEdit"
      @cancelled="closeEditAdPeriodModal"
      @succeeded="closeEditAdPeriodModal"
    />

    <AdDeletePeriodModal
      v-if="adPeriodToDelete"
      :adsStorePath="adsStorePath"
      :adPeriod="adPeriodToDelete"
      @cancelled="closeDeleteAdPeriodModal"
      @succeeded="closeDeleteAdPeriodModal"
    />
  </div>
</template>

<script>
import dayjs from 'dayjs'

import Spinner from '@/components/utils/Spinner'
import AdEditPeriodModal from './AdEditPeriodModal'
import AdDeletePeriodModal from './AdDeletePeriodModal'

import {
  SAVE_AD,
  LOAD_ADS,
  LOAD_ADS_META_DATA,
  SELECT_AD_WITH_ID,
  GET_SELECTED_AD,
  GET_ADS_META_DATA_LOAD_RESULT,
  UPLOAD_IMAGE,
  ADD_LANGUAGE_CODE,
  LOAD_SELECTED_AD_PERIODS,
  IS_LOADING_SELECTED_AD_PERIODS,
  GET_SELECTED_AD_PERIODS_LOAD_RESULT,
  GET_SELECTED_AD_PERIODS_LOAD_LOAD_ERROR
} from '@/modules/ads/adsStore'

export default {
  name: 'AdDetails',
  components: {
    Spinner,
    AdEditPeriodModal,
    AdDeletePeriodModal
  },

  props: {
    adsStorePath: {
      type: String,
      required: true
    },

    adId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      adCopy: null,
      isUploadingImage: false,
      imageUploadError: null,
      isSaving: false,
      saveError: null,
      imageUpdateTimestamp: new Date(),
      imageIsLoaded: false,
      newLanguageCode: null,
      isAddingLanguageCode: false,
      addLanguageCodeError: null,
      imageLoadFailed: false,
      adPeriodToEdit: null,
      adPeriodToDelete: null
    }
  },

  mounted() {
    const setSelectedAdIdAction = `${this.adsStorePath}/${SELECT_AD_WITH_ID}`
    this.$store.dispatch(setSelectedAdIdAction, { adId: this.adId })

    const loadPeriodsAction = `${this.adsStorePath}/${LOAD_SELECTED_AD_PERIODS}`
    this.$store.dispatch(loadPeriodsAction)

    this.resetUI()
  },

  methods: {
    resetUI() {
      if (!this.selectedAd) {
        this.adCopy = null
        return
      }

      this.adCopy = JSON.parse(JSON.stringify(this.selectedAd))

      this.imageLoadFailed = false
    },

    reloadAds() {
      const loadAdsAction = `${this.adsStorePath}/${LOAD_ADS}`
      this.$store.dispatch(loadAdsAction)
    },

    reloadAdsMetaData() {
      const loadAdsMetaDataAction = `${this.adsStorePath}/${LOAD_ADS_META_DATA}`
      this.$store.dispatch(loadAdsMetaDataAction)
    },

    isNonEmptyString(name) {
      if (!name) {
        return false
      }

      return name.trim().length > 0
    },

    async saveAd() {
      if (this.hasValidationErrors) {
        return
      }

      this.isSaving = true
      this.saveError = null

      if (!this.adCopy.languageFilterEnabled) {
        this.adCopy.languageCodes = []
      }

      try {
        const saveAdAction = `${this.adsStorePath}/${SAVE_AD}`
        await this.$store.dispatch(saveAdAction, {
          ad: this.adCopy
        })
        this.reloadAds()
      } catch (error) {
        this.saveError = error || 'Something went wrong.'
      } finally {
        this.isSaving = false
      }
    },

    async uploadImage(files) {
      if (files.length === 0) {
        return
      }

      const imageFile = files[0]
      const uploadImageAction = `${this.adsStorePath}/${UPLOAD_IMAGE}`

      this.isUploadingImage = true
      this.imageUploadError = null
      try {
        await this.$store.dispatch(uploadImageAction, { adId: this.adId, imageFile })

        // Hack to trigger reload of image
        this.imageUpdateTimestamp = new Date()

        this.imageIsLoaded = false
        this.imageLoadFailed = false
      } catch (error) {
        this.imageUploadError = error || 'Something went wrong.'
      } finally {
        this.isUploadingImage = false
      }
    },

    onImageLoaded() {
      this.imageLoadFailed = false
      this.imageIsLoaded = true
    },

    onImageLoadError() {
      this.imageLoadFailed = true
    },

    async addLanguageCode() {
      if (!this.isNewLanguageCodeValid) {
        return
      }

      this.isAddingLanguageCode = true
      this.addLanguageCodeError = null

      try {
        const addLanguageCodeAction = `${this.adsStorePath}/${ADD_LANGUAGE_CODE}`
        await this.$store.dispatch(addLanguageCodeAction, {
          languageCode: this.newLanguageCode
        })

        this.reloadAdsMetaData()
        this.newLanguageCode = null
      } catch (error) {
        this.addLanguageCodeError = error || 'Something went wrong.'
      } finally {
        this.isAddingLanguageCode = false
      }
    },

    addPeriod() {
      if (!this.adCopy) {
        return
      }

      const now = dayjs(new Date()).toDate()

      this.adPeriodToEdit = {
        adId: this.adCopy.id,
        startTimestamp: now,
        endTimestamp: now
      }
    },

    editPeriod(period) {
      this.adPeriodToEdit = period
    },

    closeEditAdPeriodModal() {
      this.adPeriodToEdit = null
    },

    deletePeriod(period) {
      this.adPeriodToDelete = period
    },

    closeDeleteAdPeriodModal() {
      this.adPeriodToDelete = null
    },

    periodStatusText(period) {
      if (this.periodIsActive(period)) {
        return 'active'
      }

      if (this.periodIsPlanned(period)) {
        return 'planned'
      }

      return null
    },

    periodStatusTagClass(period) {
      const isActive = this.periodIsActive(period)
      const isPlanned = this.periodIsPlanned(period)

      return {
        'is-success': isActive,
        'is-warning': isPlanned
      }
    },

    periodIsActive(period) {
      if (!this.adsMetaData) {
        return false
      }

      const periodId = period.id
      return this.adsMetaData.activePeriods.find(period => period.id === periodId)
    },

    periodIsPlanned(period) {
      if (!this.adsMetaData) {
        return false
      }

      const periodId = period.id
      return this.adsMetaData.plannedPeriods.find(period => period.id === periodId)
    }
  },

  computed: {
    adsMetaData() {
      const getAdsMetaData = `${this.adsStorePath}/${GET_ADS_META_DATA_LOAD_RESULT}`
      return this.$store.getters[getAdsMetaData]
    },

    selectedAd() {
      const getSelectedAd = `${this.adsStorePath}/${GET_SELECTED_AD}`
      return this.$store.getters[getSelectedAd]
    },

    adImageURL() {
      if (!this.selectedAd) {
        return null
      }

      if (!this.adsMetaData) {
        return null
      }

      return `${this.adsMetaData.imageBaseURL}ads/${this.selectedAd.id}/loadImage?u=${this.imageUpdateTimestamp}`
    },

    hasValidationErrors() {
      return this.isTargetURLValid == false || this.isTitleValid == false
    },

    isTargetURLValid() {
      return this.isNonEmptyString(this.adCopy.targetURL)
    },

    isTitleValid() {
      return this.isNonEmptyString(this.adCopy.title)
    },

    isNewLanguageCodeValid() {
      if (!this.newLanguageCode) {
        return false
      }

      return this.newLanguageCode.trim().length > 1
    },

    isDirty() {
      return JSON.stringify(this.selectedAd) !== JSON.stringify(this.adCopy)
    },

    adTextAvailable() {
      if (!this.adsMetaData) {
        return false
      }

      if (!this.adCopy.adTypeId) {
        return false
      }

      const selectedAdType = this.adsMetaData.types.find(
        adType => adType.id === this.adCopy.adTypeId
      )
      if (!selectedAdType) {
        return false
      }

      return selectedAdType.supportsText
    },

    isLoadingPeriods() {
      const isLoadingPeriods = `${this.adsStorePath}/${IS_LOADING_SELECTED_AD_PERIODS}`
      return this.$store.getters[isLoadingPeriods]
    },

    periods() {
      const getPeriods = `${this.adsStorePath}/${GET_SELECTED_AD_PERIODS_LOAD_RESULT}`
      return this.$store.getters[getPeriods]
    },

    sortedPeriods() {
      if (!this.periods) {
        return null
      }

      var sortedPeriods = [...this.periods]

      sortedPeriods.sort((period1, period2) => {
        if (period1.startTimestamp < period2.startTimestamp) {
          return 1
        }
        if (period1.startTimestamp > period2.startTimestamp) {
          return -1
        }

        return 0
      })

      return sortedPeriods
    },

    periodsLoadError() {
      const getPeriodsLoadError = `${this.adsStorePath}/${GET_SELECTED_AD_PERIODS_LOAD_LOAD_ERROR}`
      return this.$store.getters[getPeriodsLoadError]
    }
  },

  watch: {
    selectedAd() {
      this.resetUI()
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/style.scss';

.main {
  padding: 1em;
}

.image-container {
  position: relative;

  .image-scroll-area {
    overflow: auto;
  }

  .overlay {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 6px;
    display: flex;
    align-content: center;
    align-items: center;

    background-color: hsla(0, 0, 0, 0.5);
  }
}

.adImage {
  width: 960px;
  height: 540px;
  max-width: 960px;
  max-height: 540px;
  min-width: 960px;
  min-height: 540px;
}

.image-missing {
  margin-bottom: 1em;
}

.button-row {
  margin-top: 3em;
  padding-top: 1em;
  border-top: solid 1px $grey-lightest;
}

.file {
  .file-label {
    margin-right: 4px;
  }
}

.file.is-primary .file-input[disabled] ~ .file-cta:hover,
.file.is-primary .file-input[disabled] ~ .file-cta.is-hovered,
.file.is-primary .file-input[disabled] ~ .file-cta:focus,
.file.is-primary .file-input[disabled] ~ .file-cta.is-focused,
.file.is-primary .file-input[disabled] ~ .file-cta:active,
.file.is-primary .file-input[disabled] ~ .file-cta.is-active {
  background-color: whitesmoke;
  border-color: whitesmoke;
  color: #7a7a7a;
}
.file-input[disabled] ~ .file-cta,
.file-input[disabled] ~ .file-name {
  background-color: whitesmoke;
  border-color: whitesmoke;
  color: #7a7a7a;
}

.file-input[disabled] ~ .file-cta,
.file-input[disabled] ~ .file-name,
.file-input[disabled] ~ .file-cta .file-label {
  cursor: not-allowed;
}

.periods-table {
  td {
    a {
      margin-right: 1em;
    }
  }
}
</style>
